/**
 * High Cost — Estratificación social (CONTRATO PROPUESTO · pendiente backend).
 *
 * ⚠️ IMPORTANTE: los endpoints de leer/guardar el perfil SDOH NO están en la
 * doc de API actual (`API_ENDPOINTS_HIGH_COST.md` solo expone el POST
 * `suggest-social-stratification`). Este service define el contrato que el
 * front NECESITA para el editor de Estratificación; los nombres/rutas son una
 * PROPUESTA y deben confirmarse/ajustarse cuando backend publique los SPs
 * sociales (`SP_GET_PATIENT_SOCIAL_PROFILE`, `SP_SAVE_SDOH_DIMENSION`,
 * `SP_SAVE_SOCIAL_GLOBAL`, `SP_GET_SOCIAL_PROFILE_HISTORY`).
 *
 * Ver: docs/modulos/Highcost/OBSERVACIONES_FRONTEND_HIGH_COST.md
 *
 * Mientras no exista el backend, `getSocialProfile` puede fallar (404) y el
 * componente cae a `buildDefaultSocialProfile()` para poder renderizar el editor.
 */
import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import { SDOH_DIMENSIONS } from "../common/constants";

const API_URL = `${API_BASE_URL}/api/HighCost`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Perfil social por defecto (las 7 dimensiones en "unknown", sin origen).
 * Se usa como fallback mientras el endpoint real no exista.
 *
 * @param {string} cipId
 * @returns {{cipId, globalCategory, globalSource, dimensions: Array}}
 */
export const buildDefaultSocialProfile = (cipId) => ({
  cipId,
  globalCategory: "Unclassified",
  globalCategoryKey: "unclassified",
  globalSource: "none", // "auto_suggested" | "confirmed" | "none"
  globalSuggestionBasis: "",
  dimensions: SDOH_DIMENSIONS.map((d) => ({
    key: d.key,
    label: d.label,
    zCode: d.zCode,
    level: 0, // 0=unknown 1=stable 2=at_risk 3=critical
    source: "none", // "auto_suggested" | "confirmed" | "none"
    suggestionBasis: "",
    justification: "",
  })),
});

/**
 * [PROPUESTO] Lee el perfil SDOH del paciente.
 * Ruta propuesta: GET /api/HighCost/{cipId}/social-profile
 *
 * Respuesta esperada (200): mismo shape que buildDefaultSocialProfile() pero
 * con los niveles/origen/base reales.
 *
 * @param {string} cipId
 */
export const getSocialProfile = async (cipId) => {
  const response = await axios.get(
    `${API_URL}/${encodeURIComponent(cipId)}/social-profile`,
    { headers: authHeaders() }
  );
  return response.data ?? buildDefaultSocialProfile(cipId);
};

/**
 * [PROPUESTO] Guarda el nivel de UNA dimensión SDOH (con justificación obligatoria).
 * Ruta propuesta: POST /api/HighCost/{cipId}/social-dimension
 * Body propuesto: { dimensionKey, level, justification }
 *
 * El backend debe validar la justificación (no vacía) y registrar el
 * NUM_ID_EMPLOYEE del token (auditoría HIPAA). Convención del módulo: result=1 OK.
 *
 * @param {string} cipId
 * @param {{dimensionKey:string, level:number, justification:string}} body
 */
export const saveSocialDimension = async (cipId, body) => {
  const response = await axios.post(
    `${API_URL}/${encodeURIComponent(cipId)}/social-dimension`,
    body,
    { headers: authHeaders() }
  );
  return response.data ?? { result: 0, message: "Empty response" };
};

/**
 * [PROPUESTO] Sobreescribe manualmente la categoría social global.
 * Ruta propuesta: POST /api/HighCost/{cipId}/social-global
 * Body propuesto: { globalCategoryKey, justification }
 *
 * @param {string} cipId
 * @param {{globalCategoryKey:string, justification:string}} body
 */
export const saveSocialGlobal = async (cipId, body) => {
  const response = await axios.post(
    `${API_URL}/${encodeURIComponent(cipId)}/social-global`,
    body,
    { headers: authHeaders() }
  );
  return response.data ?? { result: 0, message: "Empty response" };
};

/**
 * [PROPUESTO] Historial de cambios del perfil social.
 * Ruta propuesta: GET /api/HighCost/{cipId}/social-history
 * Respuesta esperada (200): [ { changedAt, field, fromValue, toValue, changedBy, justification } ]
 *
 * @param {string} cipId
 */
export const getSocialProfileHistory = async (cipId) => {
  const response = await axios.get(
    `${API_URL}/${encodeURIComponent(cipId)}/social-history`,
    { headers: authHeaders() }
  );
  return response.data ?? [];
};
